"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
const minimalInputClasses = "border-0 border-b border-border/40 rounded-md px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary shadow-md text-foreground placeholder:text-muted-foreground/50 md:text-lg h-12 transition-colors";
function ConnectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="py-16 text-center animate-in fade-in duration-700">
                <h3 className="font-default text-3xl md:text-4xl text-primary mb-4">Transmission Received</h3>
                <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground h-12 px-8">
                    New Transmission
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormField control={control} name="name" rules={{
        required: "Full name required for flight log"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="sr-only">Full Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Full Name" className={minimalInputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email address required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="sr-only">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="Email Address" className={minimalInputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
                            <FormLabel className="sr-only">Phone Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="Phone Number" className={minimalInputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="reason" rules={{
        required: "Select inquiry type"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="sr-only">Inquiry Type</FormLabel>
                            <FormControl>
                                <select className={`${minimalInputClasses} w-full appearance-none cursor-pointer`} {...field}>
                                    <option value="" disabled className="bg-background text-muted-foreground">Inquiry Type</option>
                                    {["Care Planning", "Aircraft Equity", "Right Seat Pairing", "General Briefing", "Other"].map((opt, i) => <option key={i} value={opt} className="bg-background text-foreground">{opt}</option>)}
                                </select>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="message" rules={{
      required: "Please provide briefing details"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="sr-only">Message</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Provide details regarding your current situation or flight plan..." className={`${minimalInputClasses} min-h-[120px] resize-none pt-4`} {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 border border-destructive/50 bg-destructive/10 text-destructive text-sm rounded-md">
                    {submitError}
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-16 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-lg tracking-widest uppercase font-default font-bold mt-8 shadow-lg">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-3 size-5" />
                        Transmitting...
                    </> : 'Transmit'}
            </Button>
        </Form>;
}
export default function EstablishContact() {
  return <section data-section-id="226214" id="establish-contact" className="bg-background text-foreground relative py-24 md:py-32 overflow-hidden">
            {}
            <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-background/90 z-10" />
                <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/cockpit_panel_technical.png)] bg-cover bg-center blur-sm opacity-40 z-0" />
            </div>

            <div className="relative z-20 container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
                <div className="text-center mb-16">
                    <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase mb-4">
                        Establish Contact
                    </h2>
                    <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto font-default font-medium">
                        Direct line for pilots and families. Reach our crew at 888-370-2846 or transmit your inquiry below for a technical briefing.
                    </p>
                </div>

                <div className="bg-card/60 backdrop-blur-md p-8 md:p-12 border border-border/40 rounded-md shadow-xl">
                    <FormProvider formKey="contact_form_flight_deck" formId="contact_form_flight_deck" sectionName="Establish Contact" defaultValues={{
          name: "",
          email: "",
          phone: "",
          reason: "",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          reason: {
            required: true
          },
          message: {
            required: true
          }
        }} submitText="Transmit" successMessage="Transmission confirmed. A Care Advisor will contact you shortly to review your flight plan.">
                        <ConnectForm />
                    </FormProvider>
                </div>
            </div>
        </section>;
}